import { supabase } from './supabase';

export type AuditAction =
  | 'create' | 'update' | 'delete' | 'view'
  | 'login' | 'logout'
  | 'block' | 'unblock'
  | 'invite' | 'activate' | 'deactivate'
  | 'upgrade' | 'downgrade' | 'cancel_subscription'
  | 'role_change' | 'settings_change'
  | 'onboarding_complete'
  // ── Financial actions ───────────────────────────────────────────────────
  | 'delete_transaction'
  | 'delete_transfer'
  | 'pay_card_invoice'
  | 'delete_goal_contribution'
  | 'delete_goal';

export type AuditResource =
  | 'plan' | 'subscription' | 'payment' | 'user' | 'admin'
  | 'gateway' | 'settings' | 'audit' | 'auth' | 'broadcast' | 'webhook'
  // ── Financial resources ─────────────────────────────────────────────────
  | 'transaction' | 'transfer' | 'card_payment' | 'goal' | 'goal_contribution';

interface AuditOptions {
  action: AuditAction;
  resource: AuditResource;
  resource_id?: string;
  details?: Record<string, unknown>;
}

/**
 * Log an admin/system action to audit_logs table.
 * Silently fails so it never interrupts the main action.
 */
export const logAudit = async (opts: AuditOptions): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('audit_logs').insert({
      user_id:     user.id,
      actor_id:    user.id,
      action:      opts.action,
      resource:    opts.resource,
      resource_id: opts.resource_id ?? null,
      target_type: opts.resource,
      target_id:   opts.resource_id ?? null,
      details:     opts.details ? JSON.stringify(opts.details) : null,
    });
  } catch {
    // never throw from audit
  }
};

/**
 * Log a user-side event to audit_logs using an explicit userId.
 * Used where the auth context is already resolved (e.g. AuthContext SIGNED_IN handler).
 * Silently fails so it never interrupts the main action.
 */
export const logUserEvent = async (
  userId: string,
  action: AuditAction,
  details?: Record<string, unknown>,
): Promise<void> => {
  try {
    await supabase.from('audit_logs').insert({
      user_id:     userId,
      actor_id:    userId,
      action,
      resource:    'user',
      resource_id: userId,
      target_type: 'user',
      target_id:   userId,
      details:     details ? JSON.stringify(details) : null,
    });
  } catch {
    // never throw from audit
  }
};

/**
 * Log a user-side event resolving the current user automatically via supabase.auth.getUser().
 * Use this in hooks/pages where the userId is not directly available.
 * Silently fails so it never interrupts the main action.
 */
export const logMyEvent = async (
  action: AuditAction,
  details?: Record<string, unknown>,
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await logUserEvent(user.id, action, details);
  } catch {
    // never throw from audit
  }
};
